/** Pack + reviews data access (static export + optional Functions API). */

import { filterPackByZips, parseMarketQuery, resolveZipSet } from "./marketFilter.js";

const PACK_URL = "/data/pack.json";
const LS_REVIEWS = "leadforge_reviews_v1";
const LS_TOKEN = "leadforge_review_token";

let _packRaw = null;
let _pack = null;
let _packFilterKey = "";

export function toast(msg, bad = false) {
  const el = document.getElementById("toast");
  if (!el) return;
  el.hidden = false;
  el.textContent = msg;
  el.classList.toggle("bad", bad);
  clearTimeout(el._t);
  el._t = setTimeout(() => {
    el.hidden = true;
  }, 2200);
}

function filterKey() {
  const q = parseMarketQuery();
  return `${q.market || ""}|${(q.zips || []).join(",")}`;
}

export async function loadPack(force = false) {
  const key = filterKey();
  if (_pack && !force && key === _packFilterKey) return _pack;

  if (!_packRaw || force) {
    const res = await fetch(PACK_URL, { cache: "no-cache" });
    if (!res.ok) throw new Error(`Failed to load pack.json (${res.status})`);
    _packRaw = await res.json();
  }

  const resolved = await resolveZipSet(parseMarketQuery());
  let pack = _packRaw;
  if (resolved.zips) {
    pack = filterPackByZips(_packRaw, resolved.zips);
    pack = {
      ...pack,
      territory_id: resolved.marketId || pack.territory_id,
      metro: pack.metro,
      _market: {
        id: resolved.marketId,
        name: resolved.marketName,
        source: resolved.source,
        zip_count: resolved.zips.size,
        filtered_from: (_packRaw.leads || []).length,
      },
    };
  } else {
    pack = { ..._packRaw, _market: null };
  }
  _pack = pack;
  _packFilterKey = key;
  return _pack;
}

/** Force reload raw pack + re-apply market filter (call on hash change). */
export function clearPackCache() {
  _pack = null;
  _packRaw = null;
  _packFilterKey = "";
}

export function money(n) {
  if (n == null || n === "") return "—";
  return "$" + Number(n).toLocaleString();
}

export function tierClass(tier) {
  if (tier === "hot") return "b-hot";
  if (tier === "watch") return "b-watch";
  return "b-std";
}

function localDecisions() {
  try {
    return JSON.parse(localStorage.getItem(LS_REVIEWS) || "[]");
  } catch {
    return [];
  }
}

function saveLocalDecisions(list) {
  localStorage.setItem(LS_REVIEWS, JSON.stringify(list));
}

export function getReviewToken() {
  return localStorage.getItem(LS_TOKEN) || "";
}

export function setReviewToken(t) {
  if (t) localStorage.setItem(LS_TOKEN, t);
  else localStorage.removeItem(LS_TOKEN);
}

/** Merge seed decisions from pack + local + remote */
export async function loadDecisions(pack) {
  const map = new Map();
  for (const d of pack?.reviews_seed?.decisions || []) {
    if (d?.key) map.set(d.key, d);
  }
  for (const d of localDecisions()) {
    if (d?.key) map.set(d.key, d);
  }
  try {
    const headers = {};
    const tok = getReviewToken();
    if (tok) headers["X-LeadForge-Token"] = tok;
    const res = await fetch("/api/reviews", { headers });
    if (res.ok) {
      const data = await res.json();
      for (const d of data.decisions || []) {
        if (d?.key) map.set(d.key, d);
      }
    }
  } catch {
    /* offline / no functions */
  }
  return map;
}

export async function saveDecision(decision) {
  const list = localDecisions().filter((d) => d.key !== decision.key);
  list.push(decision);
  saveLocalDecisions(list);

  try {
    const headers = { "Content-Type": "application/json" };
    const tok = getReviewToken();
    if (tok) headers["X-LeadForge-Token"] = tok;
    const res = await fetch("/api/reviews", {
      method: "POST",
      headers,
      body: JSON.stringify(decision),
    });
    if (!res.ok) throw new Error(await res.text());
    return { remote: true };
  } catch (e) {
    return { remote: false, error: String(e.message || e) };
  }
}

export function applyFilterToCards(cards, decisions, { dropRejected = true, onlyMeasureReady = false } = {}) {
  return cards.filter((c) => {
    const st = decisions.get(c.key)?.status || c.decision?.status || "pending";
    if (onlyMeasureReady) return st === "measure_ready";
    if (dropRejected && st === "rejected") return false;
    return true;
  });
}
